import { GoalsPayload, LearningPayload, GoalCategory, Timeframe } from '../types/onboarding';

/**
 * Maps old profile goals structure to new GoalsPayload format
 */
export function toGoalsPayload(profileDoc: any): { goals: GoalsPayload; learning: LearningPayload } {
  const oldGoals = profileDoc.goals || {};
  const learningGoals = profileDoc.learningGoals || [];
  const contentPrefs = profileDoc.contentPrefs || {};
  
  // Map old boolean goals to categories
  const categories: GoalCategory[] = [];
  if (oldGoals.clients) categories.push('clients');
  if (oldGoals.networking) categories.push('partnerships');
  if (oldGoals.learning) categories.push('content');
  if (oldGoals.other) categories.push('community');
  
  // Default categories if none found
  if (categories.length === 0) {
    categories.push('clients'); // Safe default
  }
  
  const goals: GoalsPayload = {
    categories,
    timeframe: (oldGoals.timeframe as Timeframe) || "0-3m",
    kpis: oldGoals.kpis || {},
    notes: oldGoals.notes || ""
  };
  
  const learning: LearningPayload = {
    topics: learningGoals.length > 0 ? learningGoals : [],
    formatPrefs: contentPrefs.formatPrefs || [],
    timeBudget: contentPrefs.timeBudget || "1h",
    notes: contentPrefs.notes || ""
  };
  
  return { goals, learning };
}

/**
 * Merges new goals and learning data into existing profile document
 * Preserves all existing fields, only updates goals-related fields
 */
export function mergeGoalsIntoProfile(
  profileDoc: any, 
  goals: GoalsPayload, 
  learning: LearningPayload
): any {
  const existingGoals = profileDoc.goals || {};
  
  // Create new goals map preserving unknown keys
  const newGoals = { ...existingGoals };
  
  // Reset known boolean goals
  newGoals.clients = goals.categories.includes('clients');
  newGoals.partnerships = goals.categories.includes('partnerships');
  newGoals.hiring = goals.categories.includes('hiring');
  newGoals.funding = goals.categories.includes('funding');
  newGoals.speaking = goals.categories.includes('speaking');
  newGoals.content = goals.categories.includes('content');
  newGoals.personalBrand = goals.categories.includes('personalBrand');
  newGoals.productLaunch = goals.categories.includes('productLaunch');
  newGoals.community = goals.categories.includes('community');
  newGoals.networking = goals.categories.includes('partnerships'); // Backwards compat
  newGoals.learning = goals.categories.includes('content'); // Backwards compat
  
  // Set new fields
  newGoals.timeframe = goals.timeframe;
  newGoals.kpis = goals.kpis || {};
  newGoals.notes = goals.notes || "";
  
  return {
    ...profileDoc,
    goals: newGoals,
    learningGoals: learning.topics,
    contentPrefs: {
      ...profileDoc.contentPrefs,
      formatPrefs: learning.formatPrefs,
      timeBudget: learning.timeBudget,
      notes: learning.notes
    },
    lastOnboardingStep: "goals",
    updatedAt: new Date().toISOString()
  };
}

